export type TabCategory =
  | 'Development'
  | 'Research'
  | 'Shopping'
  | 'Communication'
  | 'Social'
  | 'Documentation'
  | 'News'
  | 'Entertainment'
  | 'Finance'
  | 'Education'
  | 'General';

const CATEGORY_RULES: { category: TabCategory; patterns: RegExp[] }[] = [
  {
    category: 'Development',
    patterns: [
      /github\.com/i,
      /gitlab/i,
      /stackoverflow/i,
      /codepen/i,
      /vercel\.com/i,
      /npmjs\.com/i,
      /developer\./i,
      /localhost/i,
      /127\.0\.0\.1/i,
    ],
  },
  {
    category: 'Research',
    patterns: [
      /scholar\.google/i,
      /arxiv\.org/i,
      /pubmed/i,
      /researchgate/i,
      /jstor\.org/i,
      /nature\.com/i,
      /sciencedirect/i,
      /wikipedia\.org/i,
      /\.edu\//i,
      /academia\.edu/i,
      /semanticscholar/i,
      /springer\.com/i,
      /ieee\.org/i,
    ],
  },
  {
    category: 'Shopping',
    patterns: [
      /amazon\./i,
      /ebay\./i,
      /etsy\.com/i,
      /shopify/i,
      /walmart\./i,
      /target\.com/i,
      /flipkart/i,
      /myntra/i,
      /shop\./i,
      /cart/i,
      /checkout/i,
    ],
  },
  {
    category: 'Communication',
    patterns: [
      /mail\.google/i,
      /gmail\.com/i,
      /outlook\./i,
      /slack\.com/i,
      /teams\.microsoft/i,
      /discord\.com/i,
      /zoom\.us/i,
      /meet\.google/i,
      /whatsapp/i,
      /telegram/i,
    ],
  },
  {
    category: 'Social',
    patterns: [
      /linkedin\.com/i,
      /twitter\.com/i,
      /x\.com/i,
      /facebook\.com/i,
      /instagram\.com/i,
      /reddit\.com/i,
      /tiktok\.com/i,
      /threads\.net/i,
    ],
  },
  {
    category: 'Documentation',
    patterns: [
      /notion\./i,
      /evernote/i,
      /docs\.google/i,
      /confluence/i,
      /readme\.io/i,
      /gitbook/i,
      /\/docs\b/i,
    ],
  },
  {
    category: 'News',
    patterns: [
      /news\./i,
      /bbc\./i,
      /cnn\.com/i,
      /nytimes/i,
      /theguardian/i,
      /reuters\./i,
      /techcrunch/i,
    ],
  },
  {
    category: 'Entertainment',
    patterns: [
      /youtube\.com/i,
      /netflix\.com/i,
      /spotify\.com/i,
      /twitch\.tv/i,
      /hulu\.com/i,
      /primevideo/i,
      /disneyplus/i,
    ],
  },
  {
    category: 'Finance',
    patterns: [
      /paypal/i,
      /stripe\.com/i,
      /bank/i,
      /robinhood/i,
      /coinbase/i,
      /bloomberg/i,
      /finance\.yahoo/i,
    ],
  },
  {
    category: 'Education',
    patterns: [
      /coursera/i,
      /udemy/i,
      /khanacademy/i,
      /edx\.org/i,
      /duolingo/i,
      /classroom\.google/i,
    ],
  },
];

export const CATEGORY_BADGE_CLASS: Record<TabCategory, string> = {
  Development: 'bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300',
  Research: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300',
  Shopping: 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300',
  Communication: 'bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300',
  Social: 'bg-pink-100 dark:bg-pink-900 text-pink-700 dark:text-pink-300',
  Documentation: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
  News: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300',
  Entertainment: 'bg-fuchsia-100 dark:bg-fuchsia-900 text-fuchsia-700 dark:text-fuchsia-300',
  Finance: 'bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300',
  Education: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300',
  General: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
};

/** Hex colors for graph nodes and chips (matches TabCategory). */
export const CATEGORY_GRAPH_COLOR: Record<TabCategory, string> = {
  Development: '#8b5cf6',
  Research: '#34d399',
  Shopping: '#f59e0b',
  Communication: '#38bdf8',
  Social: '#ec4899',
  Documentation: '#94a3b8',
  News: '#f97316',
  Entertainment: '#d946ef',
  Finance: '#14b8a6',
  Education: '#6366f1',
  General: '#a78bfa',
};

/** Classify a tab URL into a browsing category for grouping and analytics. */
export function categorizeTabUrl(url: string): TabCategory {
  if (!url) return 'General';
  const lower = url.toLowerCase();
  for (const rule of CATEGORY_RULES) {
    if (rule.patterns.some((p) => p.test(lower))) {
      return rule.category;
    }
  }
  return 'General';
}

export function hostFromTabUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return '';
  }
}

/** Recency-based activity score (0–100) from last update timestamp. */
export function activityFromTimestamp(updatedAt?: string): number {
  if (!updatedAt) return 0;
  const diff = Date.now() - new Date(updatedAt).getTime();
  if (Number.isNaN(diff) || diff < 0) return 0;
  const hours = diff / (1000 * 60 * 60);
  if (hours < 0.5) return 95;
  if (hours < 2) return 75;
  if (hours < 6) return 50;
  if (hours < 24) return 25;
  if (hours < 72) return 10;
  return 0;
}
